import Cookies from 'js-cookie';

/** Aynı organizasyon için eşzamanlı çağrıları tek GET'e birleştirir (ör. tablo satırları). */
const inFlightPermissionLoads = new Map();

class PermissionService {
  static PERMISSION_COOKIE_KEY = 'user_permissions';

  static ORGANIZATION_PERMISSION_KEY = 'organization_permissions';

  /**
   * Fetch user permissions from API
   * @returns {Promise<Object>} Permission data
   */
  static async fetchUserPermissions() {
    try {
      // eslint-disable-next-line no-console
      console.log('Fetching user permissions from API...');
      // eslint-disable-next-line import/no-cycle
      const { DataService } = await import('../config/dataService/dataService');
      const response = await DataService.get('/v1/profile/permission');
      // eslint-disable-next-line no-console
      console.log('Permission API response:', response.data);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error('Permission fetch failed');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching user permissions:', error);
      throw error;
    }
  }

  /**
   * Store permissions in cookie
   * @param {Object} permissionData - Permission data from API
   */
  static storePermissions(permissionData) {
    const { permissions, organization_id: organizationId } = permissionData;

    // Store current permissions
    Cookies.set(this.PERMISSION_COOKIE_KEY, JSON.stringify(permissions), { expires: 7 });

    // Store organization-permission mapping
    const existingMapping = this.getOrganizationPermissionMapping();
    const orgKey = String(organizationId);
    existingMapping[orgKey] = {
      permissions,
      timestamp: Date.now(),
    };

    Cookies.set(this.ORGANIZATION_PERMISSION_KEY, JSON.stringify(existingMapping), { expires: 30 });

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('permissions-updated'));
    }
  }

  /**
   * Get current user permissions from cookie
   * @returns {Array} Array of permission strings
   */
  static getCurrentPermissions() {
    try {
      const permissions = Cookies.get(this.PERMISSION_COOKIE_KEY);
      return permissions ? JSON.parse(permissions) : [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error parsing permissions from cookie:', error);
      return [];
    }
  }

  /**
   * Get organization-permission mapping
   * @returns {Object} Organization permission mapping
   */
  static getOrganizationPermissionMapping() {
    try {
      const mapping = Cookies.get(this.ORGANIZATION_PERMISSION_KEY);
      return mapping ? JSON.parse(mapping) : {};
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error parsing organization permission mapping:', error);
      return {};
    }
  }

  /**
   * Check if user has specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean} True if user has permission
   */
  static hasPermission(permission) {
    if (!permission || typeof permission !== 'string') {
      return false;
    }
    const permissions = this.getCurrentPermissions();
    return permissions.includes(permission);
  }

  /**
   * Check if user has any of the specified permissions
   * @param {Array} permissionList - Array of permissions to check
   * @returns {boolean} True if user has at least one permission
   */
  static hasAnyPermission(permissionList) {
    if (!permissionList || !Array.isArray(permissionList) || permissionList.length === 0) {
      return false;
    }
    const permissions = this.getCurrentPermissions();
    return permissionList.some((permission) => permissions.includes(permission));
  }

  /**
   * Check if user has all specified permissions
   * @param {Array} permissionList - Array of permissions to check
   * @returns {boolean} True if user has all permissions
   */
  static hasAllPermissions(permissionList) {
    if (!permissionList || !Array.isArray(permissionList) || permissionList.length === 0) {
      return false;
    }
    const permissions = this.getCurrentPermissions();
    return permissionList.every((permission) => permissions.includes(permission));
  }

  /**
   * Load permissions for current organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<void>}
   */
  static async loadPermissionsForOrganization(organizationId) {
    if (organizationId == null || organizationId === '') {
      return [];
    }

    const orgKey = String(organizationId);

    try {
      // eslint-disable-next-line no-console
      console.log('loadPermissionsForOrganization called with:', organizationId);

      // Check if we have cached permissions for this organization
      const mapping = this.getOrganizationPermissionMapping();
      const cachedPermissions = mapping[orgKey] ?? mapping[organizationId];

      // eslint-disable-next-line no-console
      console.log('Cached permissions for org', organizationId, ':', cachedPermissions);

      // Check if cached permissions are still valid (less than 1 hour old)
      const isValidCache = cachedPermissions && Date.now() - cachedPermissions.timestamp < 60 * 60 * 1000;

      if (isValidCache) {
        // eslint-disable-next-line no-console
        console.log('Using cached permissions for organization:', organizationId);
        // Use cached permissions
        Cookies.set(this.PERMISSION_COOKIE_KEY, JSON.stringify(cachedPermissions.permissions), { expires: 7 });
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('permissions-updated'));
        }
        return cachedPermissions.permissions;
      }

      const existing = inFlightPermissionLoads.get(orgKey);
      if (existing) {
        return existing;
      }

      // eslint-disable-next-line no-console
      console.log('Fetching fresh permissions for organization:', organizationId);

      const loadPromise = (async () => {
        const permissionData = await this.fetchUserPermissions();
        this.storePermissions(permissionData);
        return permissionData.permissions;
      })();

      inFlightPermissionLoads.set(orgKey, loadPromise);
      try {
        return await loadPromise;
      } finally {
        inFlightPermissionLoads.delete(orgKey);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading permissions for organization:', error);
      throw error;
    }
  }

  /**
   * Clear all permissions from cookies
   */
  static clearPermissions() {
    Cookies.remove(this.PERMISSION_COOKIE_KEY);
    Cookies.remove(this.ORGANIZATION_PERMISSION_KEY);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('permissions-updated'));
    }
  }

  /**
   * Clear permissions for specific organization
   * @param {string} organizationId - Organization ID
   */
  static clearOrganizationPermissions(organizationId) {
    const mapping = this.getOrganizationPermissionMapping();
    delete mapping[organizationId];
    Cookies.set(this.ORGANIZATION_PERMISSION_KEY, JSON.stringify(mapping), { expires: 30 });
  }

  /**
   * Get permissions grouped by module
   * @returns {Object} Permissions grouped by module name
   */
  static getPermissionsByModule() {
    const permissions = this.getCurrentPermissions();
    const grouped = {};

    permissions.forEach((permission) => {
      const parts = permission.split('.');
      const module = parts[0];

      if (!grouped[module]) {
        grouped[module] = [];
      }
      grouped[module].push(permission);
    });

    return grouped;
  }

  /**
   * Initialize permissions on login
   * @param {string} organizationId - Current organization ID
   * @returns {Promise<void>}
   */
  static async initializePermissions(organizationId) {
    try {
      await this.loadPermissionsForOrganization(organizationId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize permissions:', error);
      // Don't throw error, just log it
    }
  }
}

export default PermissionService;
